import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { Share } from "scmp-app/components/article/article-2.0-action-widget/share";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { RightSectionContainer } from "scmp-app/components/article/article-render/generic-article/styles";
import { GlobalChecker } from "scmp-app/components/article/article-render/global-checker";
import { checkHasLiveTag } from "scmp-app/components/article/article-render/helpers";
import {
  useArticleBaseAdvertisingInfo,
  useReduceWhitespaceByAvailableSpace,
} from "scmp-app/components/article/article-render/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { liveArticleArticle$key } from "scmp-app/queries/__generated__/liveArticleArticle.graphql";
import type { liveContentLoaderQuery$data } from "scmp-app/queries/__generated__/liveContentLoaderQuery.graphql";

import { useBotOnlyLiveArticleContext } from "./bot-only-live-article-context";
import { useLiveArticleScrollContext } from "./contexts";
import { makeSureContentItemExists, sortLiveContentsByCustomTime } from "./helpers";
import { withLiveArticleScrollContext } from "./hoc";
import { useDeepLinkLocator, useLiveContentSubscription } from "./hooks";
import { LiveContentLoader } from "./live-content-loader";
import { LiveArticleLiveUpdateSwitch } from "./live-update-switch";
import { SsrLiveContentsPreview } from "./ssr-live-contents-preview";
import {
  ActionWidgets,
  Author,
  BottomCarousel,
  Content,
  ContentContainer,
  DesktopHeaderAdSlot,
  HeaderContainer,
  Headline,
  Introduction,
  Leading,
  LiveArticleContainer,
  LiveSubscribe,
  LiveTagAndActionBar,
  LiveUpdateAlert,
  LiveUpdateAlertContainer,
  MobileContentArticleAdSlot,
  MobileContentHeaderAdSlot,
  Paywall,
  PublishDateTime,
  RightAvailableSpaceTracker,
  Sponsor,
  StyledArticleContentRightSection,
  StyledLiveArticleContent,
  StyledLiveArticleSummary,
  StyledLiveArticleTimeline,
  SubHeadline,
  TimelineContainer,
  TopicAndSection,
  UpdateTimeWrapper,
} from "./styles";
import type { EntityIdUuidMapEntry } from "./types";

export type Props = ArticleRenderChildProps & {
  reference: liveArticleArticle$key;
};

const Component: FunctionComponent<Props> = ({
  isInlinePaywallDomChanged,
  isShowInlinePaywall,
  provider,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment liveArticleArticle on Article {
        entityId
        entityUuid
        ...articleImageGalleryContent
        ...helpersCheckHasLiveTagArticle
        ...contentLiveArticleArticle
        ...globalCheckerContent
        ...newsletterWidgetLiveArticleArticle
        ...shareArticle20Content
        ...articleBaseAdvertisingInfoArticle
      }
    `,
    reference,
  );

  const [data, setData] = useState<liveContentLoaderQuery$data>();
  const [entityIdUuidMap, setEntityIdUuidMap] = useState<Record<string, EntityIdUuidMapEntry>>({});

  // Bot-only SSR: this context is only populated on verified-bot requests
  // (see the @include gate on articlePageQuery). Humans get null and never
  // render the server-side preview below.
  const botOnlyLiveArticle = useBotOnlyLiveArticleContext();

  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { zone } = useArticleBaseAdvertisingInfo(article);
  const { captureResetElement, setScrollItem } = useLiveArticleScrollContext();
  const { newUpdateCount, randomString, setNewUpdateCount, setShouldEnable } =
    useLiveContentSubscription(article.entityUuid, entityIdUuidMap ?? {});
  const {
    entityUuid: deepLinkEntityUuid,
    position: deepLinkPosition,
    reset: resetDeepLinkPosition,
  } = useDeepLinkLocator(entityIdUuidMap ?? {});

  const liveArticle = useMemo(() => data?.liveArticle ?? undefined, [data?.liveArticle]);

  const liveContents = useMemo(
    () => sortLiveContentsByCustomTime(data?.liveArticle?.liveContents),
    [data?.liveArticle?.liveContents],
  );

  const hasLiveTag = checkHasLiveTag(article);

  const isTheEnd = useMemo(
    () => data?.liveArticle?.status === false && newUpdateCount === 0,
    [data?.liveArticle?.status, newUpdateCount],
  );

  const isShowContent = useMemo(
    () => !(isShowInlinePaywall ?? false) || (isInlinePaywallDomChanged ?? false),
    [isInlinePaywallDomChanged, isShowInlinePaywall],
  );

  const { reduceWhitespaceByAvailableSpaceSpec } = useReduceWhitespaceByAvailableSpace({
    firstAdSlotSize: 1000,
    isEnabled: isShowContent,
    specs: {
      adSlotSize: 600,
      whitespaceAllowance: 100,
      widgets: [
        {
          maxHeight: 500,
          name: "beforeYouGoWidget",
        },
      ],
    },
    trackers: {
      right: RightAvailableSpaceTracker as FunctionComponent,
    },
  });

  useEffect(() => {
    setShouldEnable(isShowContent);
  }, [isShowContent, setShouldEnable]);

  useEffect(() => {
    if (!notEmpty(deepLinkPosition)) return;
    setNewUpdateCount(deepLinkPosition);
  }, [deepLinkPosition, setNewUpdateCount]);

  useEffect(() => {
    if (
      !setScrollItem ||
      !notEmpty(deepLinkEntityUuid) ||
      Object.keys(entityIdUuidMap).length === 0
    )
      return;

    const run = async () => {
      await makeSureContentItemExists(deepLinkEntityUuid);
      setScrollItem(deepLinkEntityUuid);
    };
    void run();
  }, [deepLinkEntityUuid, entityIdUuidMap, setScrollItem]);

  const scrollToLatestContent = useCallback(() => {
    resetDeepLinkPosition();
    if (typeof window === "undefined" || !window.requestAnimationFrame) return;
    if (!setScrollItem) return;
    if (!notEmpty(entityIdUuidMap)) return;

    const [latest] = Object.values(entityIdUuidMap).toSorted((a, b) => b.customTime - a.customTime);

    const run = async () => {
      await makeSureContentItemExists(latest.entityUuid);
      setScrollItem(latest.entityUuid);
      setNewUpdateCount(0);
    };
    void run();
  }, [entityIdUuidMap, resetDeepLinkPosition, setNewUpdateCount, setScrollItem]);

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const withSticky = useMemo(() => {
    if (asyncRosettaState?.status !== "success") return false;
    return (
      asyncRosettaState.result?.user === undefined ||
      asyncRosettaState.result?.user.isSCMPSubscriber === false
    );
  }, [asyncRosettaState]);

  return (
    <LiveArticleContainer>
      <HeaderContainer>
        <BaseLinkContextProvider
          customQueryParameters={{
            module: "breadcrumb",
            pgtype: "article",
          }}
        >
          <TopicAndSection>
            {provider.topic()}
            {provider.sections()}
          </TopicAndSection>
        </BaseLinkContextProvider>
        <LiveTagAndActionBar>
          {provider.liveTag({
            dimLabelWhenLiveEnds: true,
          })}
          {hasLiveTag && (
            <UpdateTimeWrapper>
              Updated&nbsp;
              {provider.actionBar({
                isBookmarkVisible: false,
                isCommentsVisible: false,
                isIconVisible: false,
              })}
            </UpdateTimeWrapper>
          )}
        </LiveTagAndActionBar>
        <Headline>{provider.headline()}</Headline>
        <SubHeadline>{provider.subHeadline()}</SubHeadline>
        <ActionWidgets>
          <Share position="article_header" reference={article} />
          {provider.actionWidget({ variant: "article-2.0" })}
          {provider.speechPlayer({
            variant: "article-2.0-minify",
          })}
          {provider.speechPlayerPortal({
            variant: "article-2.0-minify",
            withMinifyCleanUpButton: false,
          })}
        </ActionWidgets>
        <Sponsor>{provider.sponsor()}</Sponsor>
      </HeaderContainer>
      <StyledArticleContentRightSection
        {...reduceWhitespaceByAvailableSpaceSpec.right}
        entityId={article.entityId}
        provider={provider}
      >
        {shouldRenderWidgets => (
          <>
            {shouldRenderWidgets["beforeYouGoWidget"] && (
              <RightSectionContainer $stickyStackedHeight={appBarStickyHeight}>
                {provider.beforeYouGoWidget(provider, {
                  children: provider.scmpPollWidget({
                    adSlotProps: {
                      breakpoint: theme.breakpoints.up("desktop"),
                      zone,
                    },
                  }),
                })}
              </RightSectionContainer>
            )}
          </>
        )}
      </StyledArticleContentRightSection>
      <Leading>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "show",
            mobileUp: "hidden",
            tabletUp: "show",
          },
          responsiveImageVariants: {
            desktopUp: "normal",
            mobileUp: "normal",
            tabletUp: "normal",
          },
          responsiveVariants: {
            desktopUp: ImageSize["1200x800"],
            mobileUp: ImageSize["1200x800"],
            tabletUp: ImageSize["1200x800"],
          },
          toggleTitle: true,
        })}
      </Leading>
      <ContentContainer>
        {reduceWhitespaceByAvailableSpaceSpec.right.trackerNode}
        <Author>{provider.authorNames()}</Author>
        <PublishDateTime>{provider.publishedDate({ variant: "article-2.0" })}</PublishDateTime>
        <LiveUpdateAlertContainer
          $isShow={newUpdateCount > 0}
          $stickyStackedHeight={appBarStickyHeight}
        >
          <LiveUpdateAlert
            onClick={() => {
              scrollToLatestContent();
              sendGA4Tracking({
                action: "click",
                category: "liveblog",
                customized_parameters: undefined,
                subcategory: "update",
              });
            }}
          >
            {newUpdateCount > 1 ? `${newUpdateCount} New Updates` : `${newUpdateCount} New Update`}
          </LiveUpdateAlert>
        </LiveUpdateAlertContainer>
        <Introduction>Introduction</Introduction>
        <Content ref={captureResetElement}>
          {provider.body({
            extraComponentMap: {
              "inline-widget": null,
            },
          })}
        </Content>
        {botOnlyLiveArticle && <SsrLiveContentsPreview reference={botOnlyLiveArticle} />}
        <Paywall>{provider.paywall()}</Paywall>
        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <ClientSideSuspense>
              <LiveContentLoader entityUuid={article.entityUuid} randomString={randomString}>
                {({ data, entityIdUuidMap }) => {
                  setTimeout(() => {
                    setData(data);
                    setEntityIdUuidMap(entityIdUuidMap);
                  });
                  return null;
                }}
              </LiveContentLoader>
            </ClientSideSuspense>
            {liveArticle && (
              <>
                <LiveSubscribe>
                  <LiveArticleLiveUpdateSwitch
                    entityId={article.entityId}
                    reference={liveArticle}
                  />
                </LiveSubscribe>
                <StyledLiveArticleContent
                  articleReference={article}
                  deepLinkPosition={deepLinkPosition}
                  isTheEnd={isTheEnd}
                  liveArticleReference={liveArticle}
                  provider={provider}
                />
              </>
            )}
            {liveContents && (
              <>
                <StyledLiveArticleSummary reference={liveContents} />
                <TimelineContainer $stickyStackedHeight={appBarStickyHeight}>
                  <StyledLiveArticleTimeline
                    deepLinkPosition={deepLinkPosition}
                    reference={liveContents}
                  />
                </TimelineContainer>
              </>
            )}
          </GlobalChecker>
        </ClientSideSuspense>

        <MobileContentHeaderAdSlot>
          {provider.adslot({
            adUnit: "m_banner3",
            breakpoint: theme.breakpoints.down("desktop"),
            sizes: [
              [320, 100],
              [320, 50],
              [300, 100],
              [300, 50],
            ],
            withBackground: true,
          })}
        </MobileContentHeaderAdSlot>
        <MobileContentArticleAdSlot>
          {provider.adslot({
            adUnit: "m_banner1",
            breakpoint: theme.breakpoints.down("desktop"),
            sizes: [[300, 250]],
            withBackground: true,
            withLabel: true,
          })}
        </MobileContentArticleAdSlot>
        <BottomCarousel>{provider.bottomCarouselWidget(provider)}</BottomCarousel>
      </ContentContainer>
      <DesktopHeaderAdSlot withSticky={withSticky}>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [728, 90],
            [970, 90],
          ],
        })}
      </DesktopHeaderAdSlot>
    </LiveArticleContainer>
  );
};

Component.displayName = "LiveArticle";

export const LiveArticle = withLiveArticleScrollContext(Component);
